import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { type RootState } from "~/app/store";
import { METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { sendShaftUpdate } from "~/features/configurator/Shaft/api/sendShaftConfiguratorUpdate";
import { sendShaftDataReset } from "~/features/configurator/Shaft/api/sendShaftStepReset";
import { sendShaftResetConfigurator } from "~/features/configurator/Shaft/api/sendShaftResetConfigurator";
import { sendShaftStepChange } from "~/features/configurator/Shaft/api/sendShaftStepChange";
import { sendShaftAddToCart } from "~/features/configurator/Shaft/api/sendShaftAddToCart";
import { SubstepFooterInfoType, SubstepType } from "~/features/configurator/Linear/dtos";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import * as MetricCstics from "~/features/configurator/Shaft/metric/metricCsticConstants";
import { axialThread, chamfer, radialThread } from "~/features/configurator/Shaft/metric/metricCsticConstants";
import { MetricShaftSummaryData } from "~/features/configurator/Shaft/metric/components/MetricShaftSummaryData";
import { ZERO_TILE_ALWAYS_ENABLED } from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/ShaftMachiningSubstep";
import { AdditionalDataKeys, type Kmat, type LinearConfiguration } from "~/features/configurator/Linear/models";
import { ALLOWED_SHAFT_MACHINING_CODES } from "~/features/configurator/Shaft/common/commonShaftConstants";
import {
  allCsticsHaveValue,
  allCsticsHaveValueInConfiguration,
  getMainKmat,
  hasVisitedStep,
} from "~/features/configurator/Shaft/common/commonShaftHelpers";
import {
  showAxialThreadSection,
  showChamferedSection,
  showRadialThreadSection,
  validateAxialThread,
  validateChamferSegment,
  validateRadialThreadMetric,
} from "~/features/configurator/Shaft/shaftConfiguratorHelper";
import { splitBy } from "~/common/utils/reducers";
import { formatCsticValueAsNumber } from "~/features/configurator/Shaft/components/useMinMaxCsticLabel";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep3SummaryData,
} from "~/features/configurator/Shaft/metric/MetricSummaryDataProvider";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";
import { convertToSnakeCase } from "~/features/configurator/common/v2/gaHelper";
import { removeMultipleTags } from "~/common/utils/domUtils";
import {
  type SummaryData,
  type SummaryValueV2,
  transformSummaryDataToV2,
  transformToSelectionData,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import { KmatType } from "~/features/configurator/Linear/kmatType";

function prepareSummaryDataName(summaryData: SummaryData) {
  let name = summaryData.name?.toString() ?? "";
  name = removeMultipleTags(name, "p", "strong")!;
  return convertToSnakeCase(name);
}

const HARDCODED_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = HARDCODED_STEP_DATA[0].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },

  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = HARDCODED_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step3Data = getStep3SummaryData(summaryDataProvider) ?? [];

      const selectionData: SummaryValueV2[] = [];

      if (step3Data.left) {
        const prefix = prepareSummaryDataName(step3Data.left);
        [step3Data.left.chamfer, step3Data.left.axialThread]
          .filter(Boolean)
          .flatMap((data) => Object.values(data!))
          .forEach((summary: SummaryData) => {
            selectionData.push({
              name: `${step3Data.left?.name} - ${summary.name}`,
              gaName: `${prefix}_${prepareSummaryDataName(summary)}`,
              value: summary.value?.toString() ?? "",
              isStandard: summary.isStandard,
              extra: summary.extra,
            });
          });
      }

      if (step3Data.middle) {
        const prefix = prepareSummaryDataName(step3Data.middle);
        [step3Data.middle.radialThread]
          .filter(Boolean)
          .flatMap((data) => Object.values(data!))
          .forEach((summary: SummaryData) => {
            selectionData.push({
              name: `${step3Data.middle?.name} - ${summary.name}`,
              gaName: `${prefix}_${prepareSummaryDataName(summary)}`,
              value: summary.value?.toString() ?? "",
              isStandard: summary.isStandard,
              extra: summary.extra,
            });
          });
      }

      if (step3Data.right) {
        const prefix = prepareSummaryDataName(step3Data.right);
        [step3Data.right.chamfer, step3Data.right.axialThread]
          .filter(Boolean)
          .flatMap((data) => Object.values(data!))
          .forEach((summary: SummaryData) => {
            selectionData.push({
              name: `${step3Data.right?.name} - ${summary.name}`,
              gaName: `${prefix}_${prepareSummaryDataName(summary)}`,
              value: summary.value?.toString() ?? "",
              isStandard: summary.isStandard,
              extra: summary.extra,
            });
          });
      }

      selectionData.forEach((data) => {
        data.value = removeMultipleTags(data.value, "p", "span", "strong")!;
        data.name = removeMultipleTags(data.name, "p", "span", "strong")!;
      });

      return {
        name: summaryDataProvider.getGaStepName(3),
        gaName: summaryDataProvider.getGaStepName(3),
        tables: [
          {
            name: "",
            gaName: "",
            entries: selectionData,
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = HARDCODED_STEP_DATA[2].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const METRIC_SHAFT_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.MetricShaft,
  productCode: METRIC_SHAFT_CONFIGURATOR_PRODUCT_CODE,
  translationKeys: {
    pageTitle: "configurator.shaft.title.metric",
  },
  apiCalls: {
    update: sendShaftUpdate,
    reset: sendShaftResetConfigurator,
    stepChange: sendShaftStepChange,
    stepReset: sendShaftDataReset,
    addToCart: sendShaftAddToCart,
  },
  summary: MetricShaftSummaryData,
  summaryParams: () => <div />,
  isAddToCartEnabled: (state: RootState) => {
    const visitedStep2 = hasVisitedStep(2)(state);
    const hasAllCstics = allCsticsHaveValue(
      KmatType.Main,
      MetricCstics.SHAFT_LENGTH,
      MetricCstics.shaftTolerance.value,
      MetricCstics.SHAFT_MACHINING_LEFT,
      MetricCstics.SHAFT_MACHINING_MIDDLE,
      MetricCstics.SHAFT_MACHINING_RIGHT,
    )(state);

    return visitedStep2 && hasAllCstics;
  },
  isSaveEnabled: (state: RootState) => {
    const visitedStep2 = hasVisitedStep(2)(state);
    const hasLength = allCsticsHaveValue(KmatType.Main, MetricCstics.SHAFT_LENGTH)(state);

    return visitedStep2 && hasLength;
  },
  stepHardcodedData: HARDCODED_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    const avoidableCstics = [
      MetricCstics.SHAFT_LENGTH,
      MetricCstics.SHAFT_MACHINING_LEFT,
      MetricCstics.SHAFT_MACHINING_MIDDLE,
      MetricCstics.SHAFT_MACHINING_RIGHT,
      MetricCstics.chamfer.left.width.sf,
      MetricCstics.chamfer.right.width.sf,
      MetricCstics.radialThread.threadSpacing.individual.value,
    ];
    return avoidableCstics.includes(csticName);
  },
  generateDesignation: (kmats: Dict<Kmat[]>) => {
    const kmat = kmats[KmatType.Main]?.[0];

    // Create the designation using `{series}{diameter}-{material}-{left}/{middle}/{right}`;
    // Exception - skip material if it's standard
    const materialCstic = kmat.cstics[MetricCstics.MATERIAL];
    const series = kmat.cstics[MetricCstics.SERIES]?.value;
    const diameter = kmat.cstics[MetricCstics.DIAMETER]?.value;
    const material = materialCstic?.value;
    const left = kmat.cstics[MetricCstics.SHAFT_MACHINING_LEFT]?.value;
    const middle = kmat.cstics[MetricCstics.SHAFT_MACHINING_MIDDLE]?.value;
    const right = kmat.cstics[MetricCstics.SHAFT_MACHINING_RIGHT]?.value;

    const isStandardMaterial = materialCstic?.values.find((v) => v.value === material)?.standard;

    let designation = "";
    const STEPS = [
      [
        () => !!series && !!diameter,
        (currentDesignation: string) => currentDesignation + series! + diameter!.split(".")[0],
      ],
      [
        () => !!material,
        (currentDesignation: string) => currentDesignation + (isStandardMaterial ? "" : "-" + material!),
      ],
      [
        () => !!left || !!middle || !!right,
        (currentDesignation: string) => currentDesignation + "-" + [left, middle, right].filter(Boolean).join("/"),
      ],
    ] as const;

    for (let i = 0; i < STEPS.length; i += 1) {
      if (STEPS[i][0]()) {
        designation = STEPS[i][1](designation);
      } else {
        break; // All following steps are also not valid
      }
    }
    return designation || "N/A";
  },

  canQueryAvailability: (state) => {
    const kmats = state.linearConfigurator.kmats;
    const cstics = kmats[KmatType.Main][0].cstics;

    const hasAllRequiredCstics = [
      MetricCstics.SERIES,
      MetricCstics.DIAMETER,
      MetricCstics.MATERIAL,
      MetricCstics.COATING,
      MetricCstics.TOLERANCE,
    ].every((cstic) => !!cstics[cstic].value);

    return hasAllRequiredCstics;
  },
  postDefinitionProcessor: (model) => {
    const kmat = getMainKmat({
      kmats: model.kmats,
    });

    model.substeps[SubstepType.Series].tiles?.forEach((tile) => {
      tile.csticsToSetOnly = {
        [MetricCstics.DIAMETER]: null,
      };
      // Always enabled
      tile.csticsToEnable = {};
    });

    if (model.substeps[SubstepType.Diameter]) {
      model.substeps[SubstepType.Diameter].additionalData = {
        [AdditionalDataKeys.FooterType]: SubstepFooterInfoType.Text,
      };
    }

    // Filter out tiles that should not be here for now
    model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.forEach((group) => {
      group.tiles = group.tiles.filter((tile) => {
        return ALLOWED_SHAFT_MACHINING_CODES.includes(Object.values(tile.cstics)[0]!);
      });
    });

    // Clear RIGHT side if LEFT is selected with "000" code
    let tile = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[0]?.tiles.find((tile) => {
      return Object.values(tile.cstics)[0] === "000";
    });
    if (tile) {
      tile.cstics[MetricCstics.SHAFT_MACHINING_RIGHT] = null;
      tile.cstics[MetricCstics.SHAFT_MACHINING_MIDDLE] = null;
      if (ZERO_TILE_ALWAYS_ENABLED) {
        tile.csticsToEnable = {};
      }
    }
    // Clear LEFT side if RIGHT is selected with "000" code
    tile = model.substeps[SubstepType.ShaftMachining]?.groupedTiles?.[2]?.tiles.find((tile) => {
      return Object.values(tile.cstics)[0] === "000";
    });
    if (tile) {
      tile.cstics[MetricCstics.SHAFT_MACHINING_LEFT] = null;
      tile.cstics[MetricCstics.SHAFT_MACHINING_MIDDLE] = null;
      if (ZERO_TILE_ALWAYS_ENABLED) {
        tile.csticsToEnable = {};
      }
    }

    const page2 = model.steps[1];
    if (page2 && "defaultValues" in page2) {
      const defaultValues = page2.defaultValues as Record<string, string | null>;
      // Shaft Length
      defaultValues[MetricCstics.SHAFT_LENGTH] = null;
      // Shaft Machining
      defaultValues[MetricCstics.SHAFT_MACHINING_LEFT] = null;
      defaultValues[MetricCstics.SHAFT_MACHINING_MIDDLE] = null;
      defaultValues[MetricCstics.SHAFT_MACHINING_RIGHT] = null;
    }

    const page3 = model.steps[2];
    if (page3 && "defaultValues" in page3) {
      const defaultValues = page3.defaultValues as Record<string, string | null>;

      // Extra fields
      defaultValues[chamfer.left.angle] = null;
      defaultValues[chamfer.left.width.sf] = null;
      defaultValues[chamfer.right.angle] = null;
      defaultValues[chamfer.right.width.sf] = null;
      defaultValues[radialThread.size] = null;
      defaultValues[radialThread.threadSpacing.individual.value] = null;
      defaultValues[radialThread.threadSpacing.recommended] = null;
      defaultValues[radialThread.aL.value] = null;
      defaultValues[radialThread.aR.value] = null;
      defaultValues[radialThread.aLaRMode] = null;
      defaultValues[axialThread.left.size] = null;
      defaultValues[axialThread.left.depth] = null;
      defaultValues[axialThread.right.size] = null;
      defaultValues[axialThread.right.depth] = null;
    }

    model.allowedSteps = calculateAllowedSteps(model);

    const { nextSteps, previousSteps } = calculateNextPreviousSteps(model);
    model.nextSteps = nextSteps;
    model.previousSteps = previousSteps;

    // Force the default values for material
    const materialCsticValues = kmat.cstics[MetricCstics.MATERIAL]?.values;
    const seriesCsticValue = kmat.cstics[MetricCstics.SERIES]?.value;
    if (materialCsticValues) {
      materialCsticValues.forEach((value) => {
        if (value.value.startsWith("C")) {
          value.standard = true;
        }
      });
      if (seriesCsticValue === "WH") {
        const quenchedSteeltile = model.substeps[SubstepType.Material].tiles?.[0];
        if (quenchedSteeltile) {
          quenchedSteeltile.cstics[MetricCstics.MATERIAL] = "C60";
          quenchedSteeltile.csticsToEnable[MetricCstics.MATERIAL] = "C60";
        }
      }
    }

    // SCHAEFF-XXXX - Format numbers in "Recommended hole pitch jL in mm" tiles
    [radialThread.threadSpacing.recommended]
      .map((csticId) => kmat.cstics[csticId])
      .filter(Boolean)
      .flatMap((cstic) => cstic.values)
      .forEach((value) => (value.label = formatCsticValueAsNumber(value.label, 2)));

    // SCHAEFF-31144 - The standard tile should always be first
    [chamfer.left.angle, chamfer.right.angle]
      .map((csticId) => kmat.cstics[csticId])
      .filter(Boolean)
      .forEach((cstic) => {
        const [standard, nonstandard] = splitBy(cstic.values, (value) => !!value.standard);
        cstic.values = [...standard, ...nonstandard];
      });

    const lengthCstic = kmat.cstics[MetricCstics.SHAFT_LENGTH];
    if (lengthCstic) {
      lengthCstic.label = "configurator.shaft.step.2.substep.1.title.metric";
    }

    // SCHAEFF-30346 - Manually validate that the length is not too much, and create a conflict if it exceeds max
    const lengthCsticValue = kmat.cstics[MetricCstics.SHAFT_LENGTH]?.value;
    const maxLengthValue = kmat.cstics[MetricCstics.SHAFT_MAX_LENGTH]?.value;
    if (lengthCsticValue && maxLengthValue) {
      const length = +lengthCsticValue;
      const maxLength = +maxLengthValue;
      if (length > maxLength && "conflictedCstics" in model) {
        model.conflictedCstics.push({
          id: "Invalid length",
          name: "Invalid length",
          cstics: [
            {
              name: MetricCstics.SHAFT_LENGTH,
              key: MetricCstics.SHAFT_LENGTH,
            },
            {
              name: MetricCstics.SHAFT_MAX_LENGTH,
              key: MetricCstics.SHAFT_MAX_LENGTH,
            },
          ],
        });
      }
    }

    return model;
  },
  gaConfiguratorName: "shaft-metric",
} as const;

function calculateAllowedSteps(model: LinearConfiguration | LinearConfiguration): boolean[][] {
  const allowedSteps = [
    [true, false, false, false],
    [true, true, false, false],
    [true, true, true, true],
    [true, true, true, true],
  ];

  const kmat = getMainKmat({ kmats: model.kmats });

  const canVisitStep2 = allCsticsHaveValueInConfiguration(
    kmat,
    MetricCstics.SERIES,
    MetricCstics.DIAMETER,
    MetricCstics.MATERIAL,
    MetricCstics.COATING,
    MetricCstics.TOLERANCE,
  );
  const canVisitStep3 = allCsticsHaveValueInConfiguration(
    kmat,
    MetricCstics.SHAFT_LENGTH,
    MetricCstics.shaftTolerance.value,
    MetricCstics.SHAFT_MACHINING_LEFT,
    MetricCstics.SHAFT_MACHINING_MIDDLE,
    MetricCstics.SHAFT_MACHINING_RIGHT,
  );

  const leftMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_LEFT]?.value;
  const middleMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_MIDDLE]?.value;
  const rightMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_RIGHT]?.value;

  let step4Allowed: boolean | undefined = !!leftMachining && !!middleMachining && !!rightMachining;
  let hasExtraData = false;

  if (step4Allowed) {
    let conflictedCstics: string[];
    if ("conflictedCstics" in model) {
      conflictedCstics = model.conflictedCstics.flatMap((c) => c.cstics.map((c) => c.name));
    } else {
      conflictedCstics = [];
    }

    const validationCtx = { kmat, conflictedCstics, mode: "full" } as const;

    if (showChamferedSection(leftMachining)) {
      step4Allowed &&= validateChamferSegment(MetricCstics.chamfer.left, validationCtx).length === 0;
      hasExtraData = true;
    }
    if (showChamferedSection(rightMachining)) {
      step4Allowed &&= validateChamferSegment(MetricCstics.chamfer.right, validationCtx).length === 0;
      hasExtraData = true;
    }
    if (showRadialThreadSection(middleMachining)) {
      step4Allowed &&= validateRadialThreadMetric(validationCtx).length === 0;
      hasExtraData = true;
    }
    if (showAxialThreadSection(leftMachining)) {
      step4Allowed &&= validateAxialThread(axialThread.left, validationCtx).length === 0;
      hasExtraData = true;
    }
    if (showAxialThreadSection(rightMachining)) {
      step4Allowed &&= validateAxialThread(axialThread.right, validationCtx).length === 0;
      hasExtraData = true;
    }
  }

  allowedSteps[0][1] = canVisitStep2;
  allowedSteps[1][1] = canVisitStep2;
  allowedSteps[2][1] = canVisitStep2;
  allowedSteps[3][1] = canVisitStep2;

  allowedSteps[0][2] = canVisitStep3 && hasExtraData;
  allowedSteps[1][2] = canVisitStep3 && hasExtraData;
  allowedSteps[2][2] = canVisitStep3 && hasExtraData;
  allowedSteps[3][2] = canVisitStep3 && hasExtraData;

  allowedSteps[0][3] = step4Allowed ?? false;
  allowedSteps[1][3] = step4Allowed ?? false;
  allowedSteps[2][3] = step4Allowed ?? false;
  allowedSteps[3][3] = step4Allowed ?? false;

  return allowedSteps;
}
function calculateNextPreviousSteps(model: LinearConfiguration | LinearConfiguration) {
  const nextSteps = [1, 2, 3];

  const kmat = getMainKmat({ kmats: model.kmats });
  const leftMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_LEFT]?.value;
  const middleMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_MIDDLE]?.value;
  const rightMachining = kmat.cstics[MetricCstics.SHAFT_MACHINING_RIGHT]?.value;

  const showMachiningDetails =
    showChamferedSection(leftMachining) ||
    showChamferedSection(rightMachining) ||
    showRadialThreadSection(middleMachining) ||
    showAxialThreadSection(leftMachining) ||
    showAxialThreadSection(rightMachining);

  if (!showMachiningDetails) {
    nextSteps[1] = 3;
  }

  return {
    nextSteps: [...nextSteps, null],
    previousSteps: [null, ...nextSteps.map((x) => x - 1)],
  };
}

const sendStepCompletedEvent = makeSendStepCompletedEvent(METRIC_SHAFT_DEFINITION);

// TODO - Rethink previous/next steps. Maybe a step should have "enabled" aside from "Active"? Or maybe a step should have a "canVisit" function?
